import { getDateTimeFormatter } from '@fe/core';
import { GDFPirep2Record } from '../../../gdf/type';
import getTimeAgo from './common/time-ago';

export default function PirepTooltip({ record }: { record: GDFPirep2Record }) {
    const formatter = getDateTimeFormatter();
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'max-content auto', columnGap: '1rem' }}>
            <div>Reported:</div>
            <div>
                {formatter.formatTime(record.time, 'hm')} ({getTimeAgo(record.time)})
            </div>

            <div>AcType:</div>
            <div>{record.acType}</div>

            <div>Altitude:</div>
            <div>{formatAltitude(record)}</div>

            <div className="pt-2">Turbulence intensity:</div>
            <div className="pt-2">{record.turbulenceSeverity !== null && (turbulenceSeverity[record.turbulenceSeverity] ?? '-')}</div>

            <div>Turbulence type:</div>
            <div>
                {record.turbulenceType === 1 && 'Clear air turbulence (CAT)'}
                {record.turbulenceType === 2 && 'CHOP'}
            </div>

            <div className="pt-2">Icing intensity:</div>
            <div className="pt-2">{record.icingSeverity !== null && (icingSeverity[record.icingSeverity] ?? '-')}</div>
            <div>Icing type:</div>
            <div>
                {record.icingDetails === 1 && 'Clear'}
                {record.icingDetails === 2 && 'Rime'}
                {record.icingDetails === 3 && 'Mixed'}
            </div>

            <div className="pt-2">Wind direction:</div>
            <div className="pt-2">
                {record.windDir}
                {record.windDir !== null && '°'}
            </div>

            <div>Wind speed:</div>
            <div>
                {record.windSpeed} {record.windSpeed !== null ? 'kts' : ''}
            </div>

            <div>Temperature:</div>
            <div>
                {record.temp} {record.temp !== null ? '°C' : ''}
            </div>

            <div>Visibility:</div>
            <div>
                {record.visibility === 99 && 'Unrestricted'}
                {record.visibility !== null && record.visibility !== 99 && <span>{record.visibility} sm.</span>}
            </div>

            <div>Report text:</div>
            <div>{record.report}</div>
        </div>
    );
}

function formatAltitude(record: GDFPirep2Record): string {
    let upper = '';
    if (record.altitudeUpper === null) {
        upper = '';
    } else if (record.altitudeUpper === 0xfffe) {
        upper = 'Max';
    } else {
        upper = (record.altitudeUpper * 100).toLocaleString('en-US') + ' ft';
    }

    let lower = '';
    if (record.altitudeLower === null) {
        lower = '';
    } else if (record.altitudeLower === 0xfffe) {
        lower = 'Ground';
    } else if (record.altitudeLower === 0xfffd) {
        lower = 'During descent / on approach';
    } else if (record.altitudeLower === 0xfffc) {
        lower = 'During climb / on takeoff';
    } else {
        lower = (record.altitudeLower * 100).toLocaleString('en-US') + ' ft';
    }

    let result = lower;
    if (upper) {
        if (lower) {
            result += ' - ';
        }
        result += upper;
    }

    return result;
}

const turbulenceSeverity: Partial<Record<number, string>> = {
    [1]: 'Smooth (Negative)',
    [2]: 'Smooth-Light',
    [3]: 'Light',
    [4]: 'Light-Moderate',
    [5]: 'Moderate',
    [6]: 'Moderate-Severe',
    [7]: 'Severe',
    [8]: 'Severe-Extreme',
    [9]: 'Extreme'
};

const icingSeverity: Partial<Record<number, string>> = {
    [1]: 'Negative',
    [2]: 'Trace',
    [3]: 'Trace/Light',
    [4]: 'Light',
    [5]: 'Light/Moderate',
    [6]: 'Moderate',
    [8]: 'Severe',
    [7]: 'Moderate/Severe'
};
